import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';
import { setToastFunctions } from '../utils/alertUtils';

// Toast types
export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
}

interface ConfirmationDialog {
    id: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
    cancelText?: string;
    title?: string;
}

interface ToastContextType {
    toasts: Toast[];
    addToast: (toast: Omit<Toast, 'id'>) => void;
    removeToast: (id: string) => void;
    success: (title: string, message?: string) => void;
    error: (title: string, message?: string) => void;
    info: (title: string, message?: string) => void;
    warning: (title: string, message?: string) => void;
    confirm: (message: string, options?: { title?: string; confirmText?: string; cancelText?: string }) => Promise<boolean>;
    alert: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Generate unique ID
const generateId = () => Math.random().toString(36).substring(2, 9);

// Toast Provider Component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialog | null>(null);

    const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
        const id = generateId();
        const newToast: Toast = {
            id,
            duration: 4000,
            ...toast,
        };
        // Show only one notification at a time to prevent clutter
        setToasts([newToast]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const success = useCallback((title: string, message?: string) => {
        addToast({ type: 'success', title, message });
    }, [addToast]);

    const error = useCallback((title: string, message?: string) => {
        addToast({ type: 'error', title, message, duration: 6000 });
    }, [addToast]);

    const info = useCallback((title: string, message?: string) => {
        addToast({ type: 'info', title, message });
    }, [addToast]);

    const warning = useCallback((title: string, message?: string) => {
        addToast({ type: 'warning', title, message, duration: 5000 });
    }, [addToast]);

    const confirm = useCallback((message: string, options?: { title?: string; confirmText?: string; cancelText?: string }): Promise<boolean> => {
        return new Promise((resolve) => {
            const id = generateId();
            const dialog: ConfirmationDialog = {
                id,
                message,
                title: options?.title || 'Confirm',
                confirmText: options?.confirmText || 'Confirm',
                cancelText: options?.cancelText || 'Cancel',
                onConfirm: () => {
                    setConfirmationDialog(null);
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmationDialog(null);
                    resolve(false);
                },
            };
            setConfirmationDialog(dialog);
        });
    }, []);

    const alert = useCallback((message: string, title?: string) => {
        addToast({ type: 'info', title: title || 'Alert', message, duration: 5000 });
    }, [addToast]);

    // Register functions globally for use in alertUtils
    useEffect(() => {
        setToastFunctions({ alert, confirm });
    }, [alert, confirm]);

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, warning, confirm, alert }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            {confirmationDialog && <ConfirmationDialogComponent dialog={confirmationDialog} />}
        </ToastContext.Provider>
    );
};

// Hook to use toast
export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// Individual Toast Component
const ToastItem: React.FC<{ toast: Toast; onRemove: () => void }> = ({ toast, onRemove }) => {
    useEffect(() => {
        if (toast.duration) {
            const timer = setTimeout(onRemove, toast.duration);
            return () => clearTimeout(timer);
        }
    }, [toast.duration, onRemove]);

    const icons = {
        success: <CheckCircle className="h-5 w-5" />,
        error: <AlertCircle className="h-5 w-5" />,
        info: <Info className="h-5 w-5" />,
        warning: <AlertTriangle className="h-5 w-5" />,
    };

    const styles = {
        success: {
            container: 'bg-gray-900 border-gray-800',
            icon: 'bg-white/10 text-white',
            title: 'text-white',
            message: 'text-gray-300',
        },
        error: {
            container: 'bg-white border-gray-200',
            icon: 'bg-red-100 text-red-600',
            title: 'text-gray-900',
            message: 'text-gray-600',
        },
        info: {
            container: 'bg-white border-gray-200',
            icon: 'bg-gray-100 text-gray-700',
            title: 'text-gray-900',
            message: 'text-gray-600',
        },
        warning: {
            container: 'bg-white border-gray-200',
            icon: 'bg-orange-100 text-orange-600',
            title: 'text-gray-900',
            message: 'text-gray-600',
        },
    };

    const style = styles[toast.type];

    return (
        <div
            className={`
        flex items-start gap-3 p-4 rounded-2xl border shadow-xl
        animate-slide-in-right backdrop-blur-sm
        min-w-[320px] max-w-[420px]
        ${style.container}
      `}
            role="alert"
        >
            {/* Icon */}
            <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${style.icon}`}>
                {icons[toast.type]}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pt-0.5">
                <p className={`text-sm font-semibold ${style.title}`}>
                    {toast.title}
                </p>
                {toast.message && (
                    <p className={`mt-1 text-sm ${style.message}`}>
                        {toast.message}
                    </p>
                )}
            </div>

            {/* Close Button */}
            <button
                onClick={onRemove}
                className={`
          flex-shrink-0 p-1.5 rounded-lg transition-colors
          ${toast.type === 'success'
                        ? 'text-gray-400 hover:text-white hover:bg-white/10'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }
        `}
                aria-label="Close notification"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
};

// Toast Container Component
const ToastContainer: React.FC<{ toasts: Toast[]; removeToast: (id: string) => void }> = ({
    toasts,
    removeToast,
}) => {
    if (toasts.length === 0) return null;

    return (
        <div
            className="fixed top-4 right-4 z-[9999] flex flex-col gap-3"
            aria-live="polite"
            aria-atomic="true"
        >
            {toasts.map((toast) => (
                <ToastItem
                    key={toast.id}
                    toast={toast}
                    onRemove={() => removeToast(toast.id)}
                />
            ))}
        </div>
    );
};

// Confirmation Dialog Component
const ConfirmationDialogComponent: React.FC<{ dialog: ConfirmationDialog }> = ({ dialog }) => {
    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={dialog.onCancel}
            />

            {/* Dialog */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-gray-200 animate-scale-in">
                <div className="p-6">
                    {/* Title */}
                    {dialog.title && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {dialog.title}
                        </h3>
                    )}

                    {/* Message */}
                    <p className="text-gray-700 whitespace-pre-line mb-6">
                        {dialog.message}
                    </p>

                    {/* Actions */}
                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={dialog.onCancel}
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                        >
                            {dialog.cancelText}
                        </button>
                        <button
                            onClick={dialog.onConfirm}
                            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
                        >
                            {dialog.confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// CSS for animation (add to your global CSS or index.css)
// Add this to your CSS file:
/*
@keyframes slide-in-right {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in-right {
  animation: slide-in-right 0.3s ease-out forwards;
}

@keyframes scale-in {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale-in {
  animation: scale-in 0.2s ease-out forwards;
}
*/

export default ToastProvider;
